// Zod contracts for the query module (C12, architecture/modules/module-4-apps-query-attachments.md Sections 6 to 12).
// Every request and response schema lives here (architecture Section 11).
//
// C12 is the one read side for every page and MCP (AD-11, REQ-21.2): it never changes anything, and
// it applies visibility once, the same for every channel (REQ-12.2, REQ-9.6).
//
//   GET /api/apps/:appId/tickets      an app's queue (QueueQuery)     -> QueueResponse (paged + stage/app facets)
//   GET /api/tickets                  every visible app's queue        -> QueueResponse (AllTicketsQuery: QueueQuery
//                                     (AllTicketsQuery)                   plus appId[]; CT-001)
//   GET /api/tickets/:ref             ticket detail                   -> TicketDetail
//   GET /api/dashboard/tickets        the admin dashboard             -> paged DashboardRow
//   GET /api/my-requests              the reporter's own reports      -> { items: MyRequestRow[] }
//   GET /api/my-requests/:publicId    the reporter's status view      -> ReporterStatusView
// MCP (M8) calls QueryService directly: McpListQuery -> McpTicketSummary, McpTicketDetail. Triage (M5) reads
// DuplicateCandidate; the Linear sync (M14) reads HandedOffOpenTicket.
//
// Every text item carries `source` (REQ-13.5): reporter, page_capture, llm, staff, agent (an MCP client such
// as Claude acting for a person) or system.
//
// Reads accept the codes poc CT-007 retired (LEGACY_CODES) where an old database may still hold them.
import { z } from 'zod';
import { CODE_LISTS, LEGACY_CODES } from '../constants/code-lists.js';
import { AttachmentViewSchema } from './attachments.js';
import { MAX_PAGE_SIZE } from './common.js';
import { TicketLinearSchema } from './linear.js';
import { AllowedMoveSchema, TicketTypeCodeSchema } from './tickets.js';
import { StageCodeSchema, StatusSystemCodeSchema, WorkflowCodeSchema } from './workflow.js';

const Uuid = z.uuid();
const IsoDateTime = z.iso.datetime({ offset: true });
const MAX_FILTER_VALUES = 50;
const SEARCH_MAX = 200;
/** MCP's Reported as filter: at most the five reported_type codes (module-4 Section 12.1). */
const MCP_REPORTED_AS_MAX = 5;
/** MCP lists pages of 50 tickets (architecture Section 11). */
export const MCP_PAGE_SIZE = 50;

export const TEXT_SOURCES = ['reporter', 'page_capture', 'llm', 'staff', 'agent', 'system'] as const;
export const TextSourceSchema = z.enum(TEXT_SOURCES);
export type TextSource = z.infer<typeof TextSourceSchema>;
export const SourcedTextSchema = z.object({ text: z.string(), source: TextSourceSchema });
export type SourcedText = z.infer<typeof SourcedTextSchema>;

/** Who set a field: the AI triage, a person (or Claude acting for them), or the system (leaver handling...). */
export const SetBySchema = z.enum(['ai', 'person', 'system']);
/**
 * Who set the ticket's type: also the reporter, when the type is the one their pick in the drawer sets (Bug, Data
 * issue or Wording or help content: bug; Feature request: feature_request; poc CT-005, CT-019) and no person has
 * changed it since.
 */
export const TypeSetBySchema = z.enum([...SetBySchema.options, 'reporter']);
export type TypeSetBy = z.infer<typeof TypeSetBySchema>;

// ---------------------------------------------------------------------------------------------
// Query-string helpers: repeated keys (?stage=a&stage=b) or comma-separated (?stage=a,b)
// ---------------------------------------------------------------------------------------------

function listOf<T extends z.ZodType>(item: T) {
  return z
    .preprocess((value) => {
      if (value === undefined || value === '') {
        return undefined;
      }
      const raw = Array.isArray(value) ? value : [value];
      return raw
        .flatMap((v) => String(v).split(','))
        .map((v) => v.trim())
        .filter((v) => v !== '');
    }, z.array(item).max(MAX_FILTER_VALUES))
    .optional();
}
const Flag = z.stringbool().optional();
const Code = z.string().min(1).max(100);
const SortOrderSchema = z.enum(['asc', 'desc']);
const PageFields = {
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(50),
};

export const QUEUE_SORTS = ['updatedAt', 'createdAt', 'priority', 'reference'] as const;
export const QueueSortSchema = z.enum(QUEUE_SORTS);

// ---------------------------------------------------------------------------------------------
// Shared refs
// ---------------------------------------------------------------------------------------------

export const StageRefSchema = z.object({ code: StageCodeSchema, name: z.string() });
export const StatusRefSchema = z.object({ id: Uuid, name: z.string(), systemCode: StatusSystemCodeSchema.nullable() });
export const PriorityRefSchema = z.object({ code: z.string(), label: z.string(), rank: z.number().int() });
export const PrincipalRefSchema = z.object({
  principalId: Uuid,
  displayName: z.string(),
  kind: z.enum(CODE_LISTS.principal_kind),
});
export type PrincipalRef = z.infer<typeof PrincipalRefSchema>;
export const AppRefSchema = z.object({ id: Uuid, name: z.string() });
export const EnvironmentRefSchema = z.object({ code: z.string(), name: z.string() });
/** The ticket's type (bug, feature_request); null until triage or an owner sets it (poc CT-004). */
export const TypeRefSchema = z.object({ code: z.string(), name: z.string() });
export const McpClientRefSchema = z.object({ id: Uuid, displayName: z.string() });

/** Effective priority = set, or else suggested (marked as suggested, data-model 13.2). */
export const RowPrioritySchema = PriorityRefSchema.extend({ suggested: z.boolean() });

export const FlagTypeSchema = z.enum([...CODE_LISTS.flag_type, ...LEGACY_CODES.flag_type]);

// ---------------------------------------------------------------------------------------------
// Queue (REQ-8.1, REQ-8.2, root 6.6)
// ---------------------------------------------------------------------------------------------

export const QueueQuerySchema = z.object({
  /** Stage codes; covers every workflow version. */
  stage: listOf(StageCodeSchema),
  /** Status IDs from any version. */
  status: listOf(Uuid),
  /** Ticket type codes: bug, feature_request. */
  type: listOf(Code),
  /** The reporter's choice in the drawer, on the ticket's origin report (poc CT-019): reported_type codes. */
  reportedAs: listOf(z.enum(CODE_LISTS.reported_type)),
  /** Effective priority codes. */
  priority: listOf(Code),
  /** Environment codes. */
  environment: listOf(Code),
  /** How a Done ticket closed. */
  closure: listOf(z.enum(CODE_LISTS.closure_kind)),
  /** Not actioned outcome status IDs. */
  outcome: listOf(Uuid),
  /** Has linked duplicate reports or possible duplicates, or was merged into another ticket. */
  duplicates: Flag,
  autoAccepted: Flag,
  awaitingReporter: Flag,
  notTriagedByAi: Flag,
  assignedToMe: Flag,
  responsibleIsMe: Flag,
  unassigned: Flag,
  /** Full text over title, description and reports' text, or an exact reference (FB-1042). */
  q: z.string().trim().min(1).max(SEARCH_MAX).optional(),
  sort: QueueSortSchema.default('updatedAt'),
  /** Default: newest first for dates and references, most urgent first for priority. */
  order: SortOrderSchema.optional(),
  ...PageFields,
});
export type QueueQuery = z.infer<typeof QueueQuerySchema>;

/** The all-apps queue (CT-001): the app queue's filters plus the apps to narrow to (default: every visible app). */
export const AllTicketsQuerySchema = QueueQuerySchema.extend({ appId: listOf(Uuid) });
export type AllTicketsQuery = z.infer<typeof AllTicketsQuerySchema>;

export const QueueRowSchema = z.object({
  id: Uuid,
  app: AppRefSchema,
  reference: z.string(),
  revision: z.number().int(),
  /** The LLM's (or an owner's) title; "Awaiting triage" until triage writes one. */
  title: z.string(),
  awaitingTriage: z.boolean(),
  type: TypeRefSchema.nullable(),
  /** Names from the ticket's own workflow version (REQ-7.10): a feature request's stage reads Backlog, Planned... */
  stage: StageRefSchema,
  status: StatusRefSchema,
  priority: RowPrioritySchema.nullable(),
  environment: EnvironmentRefSchema,
  assignee: PrincipalRefSchema.nullable(),
  responsible: PrincipalRefSchema.nullable(),
  linkedReportCount: z.number().int(),
  /** Open flags. */
  flags: z.array(FlagTypeSchema),
  closureKind: z.enum(CODE_LISTS.closure_kind).nullable(),
  /**
   * The ticket's latest risk decision (REQ-7.4), with the detail's field names; the reason is the AI's (source
   * llm). Null for a feature request (never assessed) and for a bug not assessed yet.
   */
  risk: z.object({ result: z.enum(CODE_LISTS.risk_result), reason: SourcedTextSchema }).nullable(),
  /** The ticket's Linear issue and hand-off (poc CT-009); null when it was never handed off. */
  linear: TicketLinearSchema.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type QueueRow = z.infer<typeof QueueRowSchema>;

/** Counted by stage code across both workflows, labelled with the bug workflow's stage names (poc CT-004). */
export const StageFacetSchema = z.object({ code: StageCodeSchema, name: z.string(), count: z.number().int() });
/** Every app in the queue's scope, with its tickets under every filter except the app filter. */
export const AppFacetSchema = AppRefSchema.extend({ count: z.number().int() });

export const QueuePageSchema = z.object({
  items: z.array(QueueRowSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(MAX_PAGE_SIZE),
  total: z.number().int().min(0),
  /** Tickets per stage under every other filter (the filter panel's counts). */
  facets: z.object({ stages: z.array(StageFacetSchema), apps: z.array(AppFacetSchema) }),
});
export type QueuePage = z.infer<typeof QueuePageSchema>;
export const QueueResponseSchema = z.object({ data: QueuePageSchema });

// ---------------------------------------------------------------------------------------------
// Ticket detail (REQ-8.3, REQ-11.1, REQ-11.5, REQ-13.5, root 6.7)
// ---------------------------------------------------------------------------------------------

const DeviceSchema = z.object({
  browser: z.object({ name: z.string().nullable(), version: z.string().nullable() }),
  os: z.object({ name: z.string().nullable(), version: z.string().nullable() }),
  deviceType: z.enum(CODE_LISTS.device_type).nullable(),
  screen: z.object({ width: z.number().int().nullable(), height: z.number().int().nullable() }),
  orientation: z.enum(CODE_LISTS.orientation).nullable(),
});

export const DetailReportSchema = z.object({
  publicId: z.string(),
  /** The reporter's name only: never their contact details. */
  reporter: z.object({ principalId: Uuid, displayName: z.string() }),
  linkKind: z.enum(CODE_LISTS.report_link_kind).nullable(),
  submittedAt: IsoDateTime,
  withdrawnAt: IsoDateTime.nullable(),
  /** Exactly as written (source reporter). */
  originalText: SourcedTextSchema,
  followUp: z
    .object({
      question: SourcedTextSchema,
      answer: SourcedTextSchema.nullable(),
      outcome: z.enum(CODE_LISTS.follow_up_outcome),
      askedAt: IsoDateTime,
    })
    .nullable(),
  environment: EnvironmentRefSchema,
  installChannel: z.enum(CODE_LISTS.install_channel),
  identitySource: z.enum(CODE_LISTS.identity_source),
  /** Masked (source page_capture). */
  pageUrlStarted: SourcedTextSchema,
  pageUrlSubmitted: SourcedTextSchema,
  device: DeviceSchema,
  sdkVersion: z.string().nullable(),
  context: z.array(z.object({ key: z.string(), value: z.string(), wasMasked: z.boolean(), source: TextSourceSchema })),
  captureParts: z.array(
    z.object({
      part: z.enum(CODE_LISTS.capture_part),
      state: z.enum(CODE_LISTS.capture_part_state),
      detail: z.string().nullable(),
    }),
  ),
  console: z.array(
    z.object({
      sequence: z.number().int(),
      level: z.enum(CODE_LISTS.console_level),
      loggedAt: IsoDateTime,
      pageUrl: z.string(),
      message: z.string(),
      stackTrace: z.string().nullable(),
      source: TextSourceSchema,
    }),
  ),
  network: z.array(
    z.object({
      sequence: z.number().int(),
      method: z.string(),
      url: z.string(),
      statusCode: z.number().int().nullable(),
      durationMs: z.number().int().nullable(),
      startedAt: IsoDateTime,
      pageUrl: z.string(),
      isFailed: z.boolean(),
      source: TextSourceSchema,
    }),
  ),
  /** Screenshots and the reporter's uploads (uploads are masking `not_masked`). */
  attachments: z.array(AttachmentViewSchema),
});
export type DetailReport = z.infer<typeof DetailReportSchema>;

export const DetailCommentSchema = z.object({
  id: Uuid,
  author: PrincipalRefSchema,
  mcpClient: McpClientRefSchema.nullable(),
  kind: z.enum([...CODE_LISTS.comment_kind, ...LEGACY_CODES.comment_kind]),
  visibility: z.enum(CODE_LISTS.comment_visibility),
  /** For reporter replies: the report it belongs to. */
  reportPublicId: z.string().nullable(),
  inReplyToCommentId: Uuid.nullable(),
  body: SourcedTextSchema,
  createdAt: IsoDateTime,
  mentions: z.array(z.object({ principalId: Uuid, displayName: z.string() })),
  attachments: z.array(AttachmentViewSchema),
});

export const HistoryEntrySchema = z.object({
  revision: z.number().int(),
  occurredAt: IsoDateTime,
  changeKind: z.enum([...CODE_LISTS.change_kind, ...LEGACY_CODES.change_kind]),
  actor: PrincipalRefSchema,
  /**
   * "Claude on behalf of Alex Chen" (REQ-11.4). Null when an app acted as itself (poc CT-014): the actor is then the
   * app's own principal, named "<client> (app)".
   */
  mcpClient: McpClientRefSchema.nullable(),
  channel: z.enum(CODE_LISTS.channel),
  fields: z.array(
    z.object({
      field: z.enum([...CODE_LISTS.ticket_field, ...LEGACY_CODES.ticket_field]),
      /** Display values. For `description`, the changed sections as JSON. */
      old: z.string().nullable(),
      new: z.string().nullable(),
      /** The LLM's reason, when it set the field (REQ-6.8). */
      reason: z.string().nullable(),
    }),
  ),
  /** Reopen notes, decline reasons... (reopen notes are source reporter). */
  reason: SourcedTextSchema.nullable(),
  isAiCorrection: z.boolean(),
});
export type HistoryEntry = z.infer<typeof HistoryEntrySchema>;

export const FixRecordViewSchema = z.object({
  markedAt: IsoDateTime,
  by: PrincipalRefSchema,
  mcpClient: McpClientRefSchema.nullable(),
  note: SourcedTextSchema.nullable(),
});

export const CapabilitiesSchema = z.object({
  canComment: z.boolean(),
  canAttach: z.boolean(),
  canAssign: z.boolean(),
  canOverride: z.boolean(),
  canMerge: z.boolean(),
  canDecide: z.boolean(),
  canMarkFixed: z.boolean(),
  canPrioritise: z.boolean(),
  canDelete: z.boolean(),
});
export type Capabilities = z.infer<typeof CapabilitiesSchema>;

export const TicketDetailSchema = z.object({
  id: Uuid,
  reference: z.string(),
  /** Quote it as expectedRevision in every command (REQ-11.7). */
  revision: z.number().int(),
  title: SourcedTextSchema,
  awaitingTriage: z.boolean(),
  /**
   * Why the ticket is in its app (REQ-6.6, REQ-6.8): who last set the `app` field and their reason (triage's
   * routing reason, source llm; a person's if they moved it). Null before triage.
   */
  app: AppRefSchema.extend({ setBy: SetBySchema.nullable(), reason: z.string().nullable() }),
  raisedInApp: AppRefSchema,
  environment: EnvironmentRefSchema,
  type: z
    .object({
      code: z.string(),
      name: z.string(),
      setBy: TypeSetBySchema.nullable(),
      reason: z.string().nullable(),
      /**
       * The AI's own type and reason when it disagreed with the reporter's pick (setBy 'reporter'), read from
       * the type's reason that triage wrote (poc CT-005). Otherwise null.
       */
      aiView: z.object({ code: TicketTypeCodeSchema, reason: z.string() }).nullable(),
    })
    .nullable(),
  /**
   * The reporter's choice in the drawer, from the ticket's origin report (poc CT-019): Bug, Data issue, Wording or
   * help content, Feature request or Not sure (also every report from before CT-005 or from an older client).
   * Null when the ticket has no origin report.
   */
  reportedAs: z.object({ code: z.enum(CODE_LISTS.reported_type), name: z.string() }).nullable(),
  stage: StageRefSchema,
  status: StatusRefSchema,
  workflowVersion: z.object({ id: Uuid, number: z.number().int().nullable() }),
  /** The workflow the ticket follows, by its type's code, and its version (poc CT-004). */
  workflow: z.object({ code: WorkflowCodeSchema, versionNumber: z.number().int().nullable() }),
  priority: z.object({
    effective: RowPrioritySchema.nullable(),
    set: PriorityRefSchema.nullable(),
    /** The LLM's suggestion, with its reason (source llm); the reason is null when triage never gave one. */
    suggested: PriorityRefSchema.extend({ reason: z.string().nullable() }).nullable(),
  }),
  risk: z
    .object({
      result: z.enum(CODE_LISTS.risk_result),
      reason: SourcedTextSchema,
      policyVersion: z.string(),
      assessedAt: IsoDateTime,
    })
    .nullable(),
  responsible: PrincipalRefSchema.extend({ setBy: SetBySchema.nullable(), reason: z.string().nullable() }).nullable(),
  /** Who the ticket is handed to: always set while In progress (poc CT-007). */
  assignee: PrincipalRefSchema.nullable(),
  assignedAt: IsoDateTime.nullable(),
  flags: z.array(z.object({ flag: FlagTypeSchema, setAt: IsoDateTime })),
  closureKind: z.enum(CODE_LISTS.closure_kind).nullable(),
  mergedInto: z.object({ reference: z.string() }).nullable(),
  mergedFrom: z.array(z.object({ reference: z.string() })),
  finishedAt: IsoDateTime.nullable(),
  reopenCount: z.number().int(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  attachmentsRemovedAt: IsoDateTime.nullable(),
  /** Sections in order: doing, happened, where, expected, steps_to_reproduce. */
  description: z.array(
    z.object({
      section: z.enum(CODE_LISTS.description_section),
      content: z.string(),
      source: TextSourceSchema,
      reason: z.string().nullable(),
    }),
  ),
  reports: z.array(DetailReportSchema),
  /** Staff files and reply files not (yet) on a comment. */
  attachments: z.array(AttachmentViewSchema),
  duplicateCandidates: z.array(
    z.object({ reference: z.string(), title: z.string(), matchStrength: z.number(), reason: z.string() }),
  ),
  comments: z.array(DetailCommentSchema),
  history: z.array(HistoryEntrySchema),
  fixRecords: z.array(FixRecordViewSchema),
  /** What the caller's role allows here; which moves the ticket's version allows is GET .../allowed-moves. */
  capabilities: CapabilitiesSchema,
  /**
   * The ticket's Linear issue and hand-off (poc CT-009); null when it was never handed off. While the issue
   * exists and the ticket isn't finished, it's "Managed in Linear": status and assignee change there.
   */
  linear: TicketLinearSchema.nullable(),
});
export type TicketDetail = z.infer<typeof TicketDetailSchema>;
export const TicketDetailResponseSchema = z.object({ data: TicketDetailSchema });

/** MCP's get-ticket: the detail plus the moves the caller may make now (REQ-11.6, REQ-11.7). */
export const McpTicketDetailSchema = TicketDetailSchema.extend({ allowedMoves: z.array(AllowedMoveSchema) });
export type McpTicketDetail = z.infer<typeof McpTicketDetailSchema>;

// ---------------------------------------------------------------------------------------------
// Dashboard (REQ-22.1, root 6.8)
// ---------------------------------------------------------------------------------------------

export const DashboardQuerySchema = z.object({
  appId: listOf(Uuid),
  stage: listOf(StageCodeSchema),
  status: listOf(Uuid),
  assigneeId: Uuid.optional(),
  /** `person`: assigned to someone; `nobody`: unassigned. */
  assigneeKind: z.enum(['person', 'nobody']).optional(),
  responsibleId: Uuid.optional(),
  /** Ticket type codes: bug, feature_request (poc CT-004). */
  type: listOf(Code),
  priority: listOf(Code),
  environment: listOf(Code),
  q: z.string().trim().min(1).max(SEARCH_MAX).optional(),
  sort: QueueSortSchema.default('updatedAt'),
  order: SortOrderSchema.optional(),
  ...PageFields,
});
export type DashboardQuery = z.infer<typeof DashboardQuerySchema>;

export const DashboardRowSchema = z.object({
  id: Uuid,
  app: AppRefSchema,
  reference: z.string(),
  revision: z.number().int(),
  title: z.string(),
  awaitingTriage: z.boolean(),
  type: TypeRefSchema.nullable(),
  stage: StageRefSchema,
  status: StatusRefSchema,
  assignee: PrincipalRefSchema.nullable(),
  responsible: PrincipalRefSchema.nullable(),
  priority: RowPrioritySchema.nullable(),
  environment: EnvironmentRefSchema,
  updatedAt: IsoDateTime,
  warnings: z.object({
    /** The Platform admins principal is responsible (the fallback owner role is vacant). */
    platformAdminsResponsible: z.boolean(),
  }),
  /** The ticket's Linear issue and hand-off (poc CT-009); null when it was never handed off. */
  linear: TicketLinearSchema.nullable(),
});
export type DashboardRow = z.infer<typeof DashboardRowSchema>;

export const DashboardPageSchema = z.object({
  items: z.array(DashboardRowSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(MAX_PAGE_SIZE),
  total: z.number().int().min(0),
});
export const DashboardResponseSchema = z.object({ data: DashboardPageSchema });

// ---------------------------------------------------------------------------------------------
// Reporter reads (REQ-9.2, REQ-9.5, REQ-9.6, root 6.12)
// ---------------------------------------------------------------------------------------------

/**
 * Reporters see stages, never status names (REQ-9.2), except a finished stage's status shown to reporters
 * (`outcome`). Stage names come from the ticket's own workflow: a feature request reads Backlog, Planned.
 */
export const MyRequestRowSchema = z.object({
  publicId: z.string(),
  /** Null only in the moments before the report has a ticket. */
  reference: z.string().nullable(),
  title: z.string(),
  app: z.object({ name: z.string() }),
  /** Bug or feature request; null until triage sets it (poc CT-004). */
  type: TypeRefSchema.nullable(),
  stage: StageRefSchema,
  /**
   * In a finished stage (Done or Not actioned), the status's own name when it's shown to reporters: Won't fix,
   * Shipped, Declined, Duplicate... (poc CT-004 widened it to Done). Null otherwise, as for a bug's Closed.
   */
  outcome: z.string().nullable(),
  submittedAt: IsoDateTime,
  lastUpdatedAt: IsoDateTime,
  withdrawnAt: IsoDateTime.nullable(),
});
export type MyRequestRow = z.infer<typeof MyRequestRowSchema>;
export const MyRequestsResponseSchema = z.object({ data: z.object({ items: z.array(MyRequestRowSchema) }) });

export const ReporterStatusViewSchema = z.object({
  publicId: z.string(),
  reference: z.string().nullable(),
  /** The ticket's revision, quoted as expectedRevision by reporter commands; null until triage makes a ticket. */
  revision: z.number().int().nullable(),
  title: z.string(),
  app: z.object({ name: z.string() }),
  /** Bug or feature request; null until triage sets it (poc CT-004). */
  type: TypeRefSchema.nullable(),
  stage: StageRefSchema,
  stageHistory: z.array(z.object({ stage: StageRefSchema, at: IsoDateTime })),
  /**
   * As MyRequestRow's outcome (finished stage, shown to reporters). `reason` is the decline reason: Not actioned
   * only, only when reporters.see_decline_reason is on, and only a person's (poc CT-016: none when an app made the
   * latest change with a reason).
   */
  outcome: z.object({ name: z.string(), reason: z.string().nullable() }).nullable(),
  /** The latest fix note. */
  fixNote: z.object({ text: z.string(), at: IsoDateTime }).nullable(),
  submittedAt: IsoDateTime,
  withdrawnAt: IsoDateTime.nullable(),
  /** The person's own words. */
  originalText: z.string(),
  /** Reporter-visible comments from the team, and the person's own replies, oldest first. */
  messages: z.array(
    z.object({
      id: Uuid,
      from: z.enum(['team', 'me']),
      authorDisplayName: z.string(),
      body: z.string(),
      createdAt: IsoDateTime,
      inReplyToCommentId: Uuid.nullable(),
      attachments: z.array(AttachmentViewSchema),
    }),
  ),
  /** The person's own files: their report's screenshots and uploads, and their reply files. */
  files: z.array(AttachmentViewSchema),
  /** confirm and reopen only while Fixed, a bug's status: a feature request never offers them. */
  actions: z.object({ confirm: z.boolean(), reopen: z.boolean(), withdraw: z.boolean(), reply: z.boolean() }),
});
export type ReporterStatusView = z.infer<typeof ReporterStatusViewSchema>;
export const ReporterStatusResponseSchema = z.object({ data: ReporterStatusViewSchema });

// ---------------------------------------------------------------------------------------------
// MCP reads (M8; no HTTP route here)
// ---------------------------------------------------------------------------------------------

export const McpListQuerySchema = z.object({
  /** One app; omitted: every app the caller can see. */
  appId: Uuid.optional(),
  stage: z.array(StageCodeSchema).max(MAX_FILTER_VALUES).optional(),
  status: z.array(Uuid).max(MAX_FILTER_VALUES).optional(),
  /**
   * Ticket type codes. `bug` also covers tickets triage hasn't typed yet (they're in the bug workflow), so a
   * ticket without a type stays listable (REQ-11.1). Omitted: every type (the MCP tool defaults it, M8).
   */
  type: z.array(Code).max(MAX_FILTER_VALUES).optional(),
  /** The reporter's pick on the ticket's origin report, as the queue's Reported as filter (CT-019). */
  reportedAs: z.array(z.enum(CODE_LISTS.reported_type)).max(MCP_REPORTED_AS_MAX).optional(),
  priority: z.array(Code).max(MAX_FILTER_VALUES).optional(),
  environment: z.array(Code).max(MAX_FILTER_VALUES).optional(),
  closure: z.array(z.enum(CODE_LISTS.closure_kind)).max(MAX_FILTER_VALUES).optional(),
  duplicates: z.boolean().optional(),
  autoAccepted: z.boolean().optional(),
  awaitingReporter: z.boolean().optional(),
  notTriagedByAi: z.boolean().optional(),
  /** The caller's principal is the responsible principal. */
  responsibleIsMe: z.boolean().optional(),
  unassigned: z.boolean().optional(),
  q: z.string().trim().min(1).max(SEARCH_MAX).optional(),
  sort: QueueSortSchema.default('updatedAt'),
  order: SortOrderSchema.optional(),
  page: z.number().int().min(1).default(1),
});
export type McpListQuery = z.input<typeof McpListQuerySchema>;

export const McpTicketSummarySchema = z.object({
  id: Uuid,
  reference: z.string(),
  revision: z.number().int(),
  title: SourcedTextSchema,
  app: AppRefSchema,
  stage: StageRefSchema,
  status: StatusRefSchema,
  type: TypeRefSchema.nullable(),
  priority: RowPrioritySchema.nullable(),
  environment: EnvironmentRefSchema,
  assignee: PrincipalRefSchema.nullable(),
  workflowVersion: z.object({ id: Uuid, number: z.number().int().nullable() }),
  linkedReportCount: z.number().int(),
  flags: z.array(FlagTypeSchema),
  /** The ticket's Linear issue and hand-off (poc CT-009); null when it was never handed off. */
  linear: TicketLinearSchema.nullable(),
  updatedAt: IsoDateTime,
});
export type McpTicketSummary = z.infer<typeof McpTicketSummarySchema>;

// ---------------------------------------------------------------------------------------------
// Triage reads (M5) and the Linear sync's candidates (M14); no HTTP route
// ---------------------------------------------------------------------------------------------

/**
 * A ticket, open or finished, that may be a duplicate (REQ-6.4). No people's names: triage sends these to the LLM.
 * Only an open one (`isOpen`) can be linked or merged; a match on a finished one is listed as a possible duplicate.
 */
export const DuplicateCandidateSchema = z.object({
  ticketId: Uuid,
  reference: z.string(),
  title: z.string().nullable(),
  /** The description sections, joined, cut to a few thousand characters. */
  description: z.string(),
  stage: StageRefSchema,
  environmentCode: z.string(),
  linkedReportCount: z.number().int(),
  /** The ticket's stage is open (stages.is_open): not Done or Not actioned. */
  isOpen: z.boolean(),
  /** Text-search score in 0..1 (higher is closer); only for ordering. */
  score: z.number(),
});
export type DuplicateCandidate = z.infer<typeof DuplicateCandidateSchema>;

/** A handed-off ticket that isn't finished (its stage is neither Done nor Not actioned): the Linear sync's candidate. */
export const HandedOffOpenTicketSchema = z.object({
  ticketId: Uuid,
  reference: z.string(),
  stageCode: StageCodeSchema,
});
export type HandedOffOpenTicket = z.infer<typeof HandedOffOpenTicketSchema>;

/** A generic page of items (architecture Section 11). */
export interface Paged<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}
